const rebates = [
  {
    state: 'VIC',
    name: 'Victoria',
    flag: '🟦',
    panelRebate: 'Up to $1,400',
    batteryRebate: 'Up to $2,950',
    loan: 'Interest-free loan available',
    highlight: true,
    link: 'https://www.solar.vic.gov.au',
  },
  {
    state: 'NSW',
    name: 'New South Wales',
    flag: '🟥',
    panelRebate: 'STC discount applies',
    batteryRebate: 'Interest-free loan to $9,000',
    loan: 'Empowering Homes Program',
    highlight: false,
    link: 'https://www.energy.nsw.gov.au',
  },
  {
    state: 'QLD',
    name: 'Queensland',
    flag: '🟨',
    panelRebate: 'STC discount applies',
    batteryRebate: 'Grants up to $3,000',
    loan: 'Affordable Energy Plan loans',
    highlight: false,
    link: 'https://www.qld.gov.au/housing/buying-owning-home/solar-and-batteries',
  },
  {
    state: 'SA',
    name: 'South Australia',
    flag: '🟩',
    panelRebate: 'STC discount applies',
    batteryRebate: 'Up to $2,000 subsidy',
    loan: 'Home Battery Scheme',
    highlight: false,
    link: 'https://www.sa.gov.au/topics/energy-and-environment/using-saving-energy/solar-panels',
  },
  {
    state: 'WA',
    name: 'Western Australia',
    flag: '🟫',
    panelRebate: 'STC discount applies',
    batteryRebate: 'Distributed Energy Buyback',
    loan: 'Synergy buyback rates',
    highlight: false,
    link: 'https://www.cleanenergyregulator.gov.au',
  },
  {
    state: 'ACT',
    name: 'Australian Capital Territory',
    flag: '⬛',
    panelRebate: 'STC discount applies',
    batteryRebate: 'Sustainable Household Scheme',
    loan: 'Zero-interest loans up to $15,000',
    highlight: true,
    link: 'https://www.actsustainablehouseholdscheme.com.au',
  },
];

const federalItems = [
  {
    icon: 'ri-government-line',
    title: 'Small-scale Technology Certificates (STCs)',
    description: 'The federal STC scheme reduces the upfront cost of solar panels by $400–$600 per kW installed. This discount is applied automatically at point of sale by your installer.',
  },
  {
    icon: 'ri-recycle-line',
    title: 'Clean Energy Finance Corporation (CEFC)',
    description: 'The CEFC provides concessional finance for renewable energy projects, particularly for commercial and industrial applications looking to install large-scale solar systems.',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Instant Asset Write-Off (Business)',
    description: 'Businesses can claim an immediate tax deduction for commercial solar system costs under the federal instant asset write-off scheme, significantly improving investment returns.',
  },
];

export default function SolarRebates() {
  return (
    <section className="bg-background-100/50 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200/50 mb-5">
            <i className="ri-currency-line text-accent-700 text-sm"></i>
            <span className="text-xs font-semibold text-accent-800 tracking-wide uppercase whitespace-nowrap">Rebates & Incentives</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
            Solar Rebates &amp; Incentives<br className="hidden md:block" /> by Australian State (2025–2026)
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Every Australian state offers a mix of federal and state-level incentives that can significantly reduce the cost of going solar. Here's what's available where you live.
          </p>
        </div>

        {/* State rebate cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {rebates.map((r) => (
            <div
              key={r.state}
              className={`rounded-2xl p-5 border transition-all duration-200 ${
                r.highlight
                  ? 'bg-primary-50 border-primary-200'
                  : 'bg-background-50 border-background-200/70'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="font-heading text-xl font-extrabold text-foreground-950">{r.state}</span>
                  <p className="text-xs text-foreground-500 mt-0.5">{r.name}</p>
                </div>
                {r.highlight && (
                  <span className="text-xs font-semibold bg-primary-500 text-white px-2.5 py-1 rounded-full whitespace-nowrap">Best Value</span>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-2.5">
                  <i className="ri-sun-line text-primary-500 text-base mt-0.5 shrink-0"></i>
                  <div>
                    <span className="text-xs font-semibold text-foreground-700 block">Solar Panels</span>
                    <span className="text-sm text-foreground-800 font-medium">{r.panelRebate}</span>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <i className="ri-battery-charge-line text-accent-700 text-base mt-0.5 shrink-0"></i>
                  <div>
                    <span className="text-xs font-semibold text-foreground-700 block">Battery Storage</span>
                    <span className="text-sm text-foreground-800 font-medium">{r.batteryRebate}</span>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <i className="ri-bank-line text-secondary-600 text-base mt-0.5 shrink-0"></i>
                  <div>
                    <span className="text-xs font-semibold text-foreground-700 block">Finance</span>
                    <span className="text-sm text-foreground-800 font-medium">{r.loan}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Federal incentives */}
        <div className="bg-foreground-950 rounded-2xl p-7 md:p-10">
          <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2">Federal Incentives (All States)</h3>
          <p className="text-sm text-white/60 mb-8">These federal programs apply Australia-wide and stack on top of state rebates.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {federalItems.map((item) => (
              <div key={item.title}>
                <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-primary-400 text-lg`}></i>
                </div>
                <h4 className="font-heading text-sm font-bold text-white mb-2">{item.title}</h4>
                <p className="text-xs text-white/60 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-foreground-400 text-center mt-6">
          Rebate values are indicative as of 2025–2026. Always verify current eligibility and amounts with your state energy authority or AK Energy provider.
        </p>
      </div>
    </section>
  );
}